import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { usePhysicianAuthStore } from "@/stores/physicianAuthStore";
// Side-effect import — registers the realtime socket's auth-sync subscription
// (see socketClient.ts) before silentRestore() below can resolve a token.
import "@/lib/socketClient";
import { CaregiverProtectedLayout } from "@/portals/caregiver/layout/CaregiverProtectedLayout";
import { CaregiverDashboardPage } from "@/portals/caregiver/pages/CaregiverDashboardPage";
import { AccessActivityPage } from "@/portals/caregiver/pages/AccessActivityPage";
import { CaregiverProfilePage } from "@/portals/caregiver/pages/CaregiverProfilePage";
import { CaregiverCategoryRecordsPage } from "@/portals/caregiver/pages/CaregiverCategoryRecordsPage";
import { PatientGuestOnly } from "@/portals/patient/layout/PatientGuestOnly";
import { PatientAuthLayout } from "@/portals/patient/layout/PatientAuthLayout";
import { PatientProtectedLayout } from "@/portals/patient/layout/PatientProtectedLayout";
import { PatientSignInPage } from "@/portals/patient/pages/auth/PatientSignInPage";
import { PatientSignUpPage } from "@/portals/patient/pages/auth/PatientSignUpPage";
import { VerifyOtpPage } from "@/portals/patient/pages/auth/VerifyOtpPage";
import { ForgotPasswordPage } from "@/portals/patient/pages/auth/ForgotPasswordPage";
import { VerifyResetOtpPage } from "@/portals/patient/pages/auth/VerifyResetOtpPage";
import { ResetPasswordPage } from "@/portals/patient/pages/auth/ResetPasswordPage";
import { DeclineInvitePage } from "@/portals/patient/pages/auth/DeclineInvitePage";
import { AdminProtectedLayout } from "@/portals/admin/layout/AdminProtectedLayout";
import { AdminGuestOnly } from "@/portals/admin/layout/AdminGuestOnly";
import { sampleProfiles } from "@/data/patient/records.mock";
import { DashboardPage } from "@/portals/patient/pages/DashboardPage";
import { UploadPage } from "@/portals/patient/pages/UploadPage";
import { UploadHistoryPage } from "@/portals/patient/pages/UploadHistoryPage";
import { HumanReviewQueuePage } from "@/portals/patient/pages/HumanReviewQueuePage";
import { ConsentPrivacyPage } from "@/portals/patient/pages/ConsentPrivacyPage";
import { RecordDetailsPage } from "@/portals/patient/pages/RecordDetailsPage";
import { TimelinePage } from "@/portals/patient/pages/TimelinePage";
import { AskPage } from "@/portals/patient/pages/AskPage";
import { DiagnosisPage } from "@/portals/patient/pages/DiagnosisPage";
import { HealthConcernsPage } from "@/portals/patient/pages/HealthConcernsPage";
import { CompleteHistoryPage } from "@/portals/patient/pages/CompleteHistoryPage";
import { MedicationsPage } from "@/portals/patient/pages/MedicationsPage";
import { ReviewItemPage } from "@/portals/patient/pages/ReviewItemPage";
import { DiagnosticsPage } from "@/portals/patient/pages/DiagnosticsPage";
import { DicomViewerPage } from "@/portals/patient/pages/DicomViewerPage";
import { CareTreatmentPage } from "@/portals/patient/pages/CareTreatmentPage";
import { InsuranceCardsPage } from "@/portals/patient/pages/InsuranceCardsPage";
import { ClaimsPage } from "@/portals/patient/pages/ClaimsPage";
import { FhirExportPage } from "@/portals/patient/pages/FhirExportPage";
import { ManageFamilyPage } from "@/portals/patient/pages/ManageFamilyPage";
import { ManageCaregiversPage } from "@/portals/patient/pages/ManageCaregiversPage";
import { ProfilePage } from "@/portals/patient/pages/profile/ProfilePage";
import { AdminSignInPage } from "@/portals/admin/pages/AdminSignInPage";
import { AdminDashboardPage } from "@/portals/admin/pages/AdminDashboardPage";
import { UsersPage } from "@/portals/admin/pages/UsersPage";
import { AuditTrailPage } from "@/portals/admin/pages/AuditTrailPage";
import { PhysiciansPage } from "@/portals/admin/pages/PhysiciansPage";
import { PatientDetailPage } from "@/portals/admin/pages/PatientDetailPage";
import { ReviewQueuePage } from "@/portals/admin/pages/ReviewQueuePage";
import { PhysicianGuestOnly } from "@/portals/physician/layout/PhysicianGuestOnly";
import { PhysicianProtectedLayout } from "@/portals/physician/layout/PhysicianProtectedLayout";
import { PhysicianLoginPage } from "@/portals/physician/pages/PhysicianLoginPage";
import { PhysicianInvitePage } from "@/portals/physician/pages/PhysicianInvitePage";
import { PhysicianDeclineInvitePage } from "@/portals/physician/pages/PhysicianDeclineInvitePage";
import { PhysicianDashboardPage } from "@/portals/physician/pages/PhysicianDashboardPage";
import { PhysicianReviewItemPage } from "@/portals/physician/pages/PhysicianReviewItemPage";
import { PhysicianMyItemsPage } from "@/portals/physician/pages/PhysicianMyItemsPage";
import { PrivacyPolicyPage, TermsOfUsePage, HipaaNoticePage } from "@/portals/public/pages/LegalPolicyPages";
import { AboutPage } from "@/portals/public/pages/AboutPage";

const activeProfile = sampleProfiles[0];

// The backend's /auth/epic/callback (router.py) redirects the real Epic OAuth round
// trip to a hardcoded `${FRONTEND_BASE_URL}/dashboard?epic_connected=1` (or
// `?epic_error=<CODE>`) — this app's actual dashboard route is `/patient`, so
// without this alias that redirect 404s into the sign-in page and the status
// params (and the toast/sync they're supposed to trigger) are silently lost.
function DashboardRedirect() {
  const location = useLocation();
  return <Navigate to={`/patient${location.search}`} replace />;
}

export function AppRouter() {
  // Zustand stores don't need a Provider — kick off only the current portal's
  // silent, cookie-based session restore here instead. Only one portal is ever
  // rendered at a time, so restoring the other one's session too just wastes a
  // request and guarantees a 401 in the logs (no cookie for a portal you've
  // never logged into). Reads window.location directly rather than
  // useLocation() since this runs once on mount, before this component's own
  // BrowserRouter below exists as a context.
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) {
      void useAdminAuthStore.getState().silentRestore();
    } else if (window.location.pathname.startsWith("/physician")) {
      void usePhysicianAuthStore.getState().silentRestore();
    } else {
      // The caregiver portal reuses the patient portal's auth session
      // wholesale (see CaregiverProtectedLayout) — no separate caregiver
      // identity exists yet, so /caregiver falls in here too.
      void usePatientAuthStore.getState().silentRestore();
    }
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/patient/auth/signin" replace />} />
        {/* Public, unauthenticated documentation page — submitted to Epic as the
            app's "Public Documentation URL". Deliberately kept out of every guard
            below and out of any in-app nav; reachable only by typing the URL. */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/hipaa-notice" element={<HipaaNoticePage />} />

        {/* Patient portal */}
        <Route path="/patient">
          {/* Guest-only auth pages — all share PatientAuthLayout via Outlet */}
          <Route path="auth" element={<PatientGuestOnly />}>
            <Route element={<PatientAuthLayout />}>
              <Route path="signin" element={<PatientSignInPage />} />
              <Route path="signup" element={<PatientSignUpPage />} />
              <Route path="verify-otp" element={<VerifyOtpPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="verify-reset-otp" element={<VerifyResetOtpPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
            </Route>
          </Route>

          {/* Decline-without-login — deliberately outside PatientGuestOnly, since
              anyone (logged in or not, as any account) can click this email link. */}
          <Route element={<PatientAuthLayout />}>
            <Route path="invite/decline" element={<DeclineInvitePage />} />
          </Route>

          {/* Protected patient pages */}
          <Route element={<PatientProtectedLayout profile={activeProfile} />}>
            <Route index element={<DashboardPage />} />
            {/* "Document Panel" — every extracted file, browsable, alongside
                its extraction. Was "/patient/upload/history" ("Your
                Uploads"); renamed here as the single home for this content. */}
            <Route path="documents" element={<UploadHistoryPage />} />
            <Route path="profile" element={<ProfilePage profile={activeProfile} />} />
            <Route path="upload" element={<UploadPage />} />
            <Route path="human-review-queue" element={<HumanReviewQueuePage />} />
            <Route path="records/:recordId" element={<RecordDetailsPage />} />
            <Route path="timeline" element={<TimelinePage />} />
            <Route path="ask" element={<AskPage />} />
            <Route path="consent-privacy" element={<ConsentPrivacyPage />} />
            <Route path="diagnosis" element={<DiagnosisPage />} />
            <Route path="health-concerns" element={<HealthConcernsPage />} />
            <Route path="complete-history" element={<CompleteHistoryPage />} />
            <Route path="medications" element={<MedicationsPage />} />
            <Route path="medications/review/:itemId" element={<ReviewItemPage />} />
            <Route path="diagnostics" element={<DiagnosticsPage />} />
            {/* Not a sidebar nav destination — reached only by clicking a
                study in Recent DICOM Uploads or right after a new upload
                (see DiagnosticsImagingView.tsx's openViewer). Keeps the
                normal sidebar/header chrome like every other page here — it
                just replaces the Diagnostics & Imaging content area, with
                its own back button returning to /patient/diagnostics. */}
            <Route path="diagnostics/imaging/:studyId" element={<DicomViewerPage />} />
            <Route path="care-treatment" element={<CareTreatmentPage />} />
            <Route path="insurance-cards" element={<InsuranceCardsPage />} />
            <Route path="claims" element={<ClaimsPage />} />
            <Route path="manage-family" element={<ManageFamilyPage />} />
            <Route path="manage-caregivers" element={<ManageCaregiversPage />} />
            <Route path="fhir-export" element={<FhirExportPage />} />
          </Route>
        </Route>

        {/* Admin portal */}
        <Route path="/admin">
          <Route
            path="signin"
            element={
              <AdminGuestOnly>
                <AdminSignInPage />
              </AdminGuestOnly>
            }
          />
          <Route element={<AdminProtectedLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:patientId" element={<PatientDetailPage />} />
            <Route path="audit-trail" element={<AuditTrailPage />} />
            <Route path="physicians" element={<PhysiciansPage />} />
            <Route path="review-queue" element={<ReviewQueuePage />} />
          </Route>
        </Route>

        {/* Caregiver portal */}
        <Route path="/caregiver">
          <Route element={<CaregiverProtectedLayout />}>
            <Route index element={<CaregiverDashboardPage />} />
            <Route path="patients/:patientId/:category" element={<CaregiverCategoryRecordsPage />} />
            <Route path="access-activity" element={<AccessActivityPage />} />
            <Route path="profile" element={<CaregiverProfilePage />} />
          </Route>
        </Route>

        {/* Physician portal — invite-only: accounts come from an admin's
            invitation link, so there is no public signup route. */}
        <Route path="/physician">
          <Route element={<PatientAuthLayout />}>
            <Route
              path="login"
              element={
                <PhysicianGuestOnly>
                  <PhysicianLoginPage />
                </PhysicianGuestOnly>
              }
            />
            <Route path="invite/:token" element={<PhysicianInvitePage />} />
            <Route path="invite/:token/decline" element={<PhysicianDeclineInvitePage />} />
          </Route>
          <Route element={<PhysicianProtectedLayout />}>
            <Route index element={<PhysicianDashboardPage />} />
            <Route path="review/:itemId" element={<PhysicianReviewItemPage />} />
            <Route path="my-items" element={<PhysicianMyItemsPage />} />
            <Route path="my-items/:itemId" element={<PhysicianReviewItemPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/patient/auth/signin" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
